import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AppButton from "../../questionnaire/components/AppButton";
import CheckinLayout from "../../questionnaire/components/CheckinLayout";
import ScreenerFormItem from "../../questionnaire/components/ScreenerFormItem";
import { useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { useLoader } from "../../questionnaire/contexts/LoaderContext";
import { WorkflowType } from "../api/athena";
import { Alert } from "../../components/shared/Alert";
import axiosInstance from "../../utils/axiosConfig";
import { getApiErrorMessage } from "../../utils/apiError";
import type { ScreenerSubmitPayload, ScreenerResponse, ScreenerQuestionnaire } from "../types/screener";
import { calculateQuestionScore } from "../utils/screenerScoring";
import { isTextInputQuestion } from "../types/screener";
import {
  getIncompleteScreenerNames,
  screenerHasAllRequiredAnswers,
  shouldAllowContinueWithoutScreeners,
} from "../utils/screenerCompleteness";

const WellnessCheck: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, markStepCompleted, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  const medicalPath = useAppointmentStepPath("medical-history");
  const reviewPath = useAppointmentStepPath("review-and-sign");

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [screenerData, setScreenerData] = useState<ScreenerResponse | null>(null);
  const [allScreenersSubmitted, setAllScreenersSubmitted] = useState(false);
  const [screenerLoadFailed, setScreenerLoadFailed] = useState(false);

  const pageTopRef = useRef<HTMLDivElement>(null);
  const errorKeyRef = useRef(0);
  const [errorKey, setErrorKey] = useState(0);

  React.useEffect(() => {
    const initialLoading = workflowLoading || loading;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, show, hide]);

  // Per-screener accordion state (widened to number|string to satisfy shared ScreenerQuestionnaire types)
  const [answersByScreener, setAnswersByScreener] = useState<Record<number | string, Record<string, string>>>({});
  const [openScreener, setOpenScreener] = useState<number | string | null>(null);
  const [submittedScreeners, setSubmittedScreeners] = useState<Set<number | string>>(new Set());
  const [submittingScreeners, setSubmittingScreeners] = useState<Set<number | string>>(new Set());
  const inFlightContinueRef = useRef(false);
  const inFlightSubmittingScreenersRef = useRef<Set<number | string>>(new Set());

  const scrollToPageTop = useCallback(() => {
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pageTopRef.current?.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    if (localError || workflowError) {
      scrollToPageTop();
    }
  }, [localError, errorKey, workflowError, scrollToPageTop]);

  const setBlockingError = useCallback((message: string) => {
    errorKeyRef.current += 1;
    setErrorKey(errorKeyRef.current);
    setLocalError(message);
  }, []);

  /* ---------------------- Fetch screener data ---------------------- */

  React.useEffect(() => {
    const fetchScreenerQuestions = async () => {
      if (!appointmentId) return;

      setLoading(true);
      setLocalError(null);
      setScreenerLoadFailed(false);
      try {
        const response = await axiosInstance.get(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`, {
          headers: { client: 'axis', domain: 'axis' }
        });

        // Check if response is an empty array (all screeners already submitted)
        if (Array.isArray(response.data) && response.data.length === 0) {
          setAllScreenersSubmitted(true);
          setScreenerData(null);
        } else {
          // Normalize the response - handle both array and object formats
          const raw = Array.isArray(response.data) 
            ? { questionnairescreeners: response.data }
            : (response.data as ScreenerResponse);

          // Normalize CUSTOM screener items so shared components work uniformly
          raw.questionnairescreeners = raw.questionnairescreeners.map(
            (q: ScreenerQuestionnaire & Record<string, any>) => {
              if (q.screenertype === 'CUSTOM') {
                return {
                  ...q,
                  name: q.templatename || q.name || 'Custom Screener',
                  questionnaireid: q.questionnaireid ?? 0,
                };
              }
              return q;
            },
          );

          const data = raw;
          setScreenerData(data);
          setAllScreenersSubmitted(false);

          // Initialize per-screener answer maps
          const initial: Record<number | string, Record<string, string>> = {};
          (data.questionnairescreeners ?? []).forEach((q) => {
            const tid = q.templateid ?? q.questionnaireid;
            initial[tid] = {};
          });
          setAnswersByScreener(initial);
        }
      } catch (err) {
        setScreenerLoadFailed(true);
        setScreenerData(null);
        setLocalError(
          getApiErrorMessage(
            err,
            "Unable to load wellness questions. You can continue check-in and complete this step later if needed.",
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchScreenerQuestions();
  }, [appointmentId]);

  /* ---------------------- Answer change handler -------------------- */

  const handleAnswerChange = useCallback(
    (templateId: number | string, answerKey: string, value: string) => {
      setAnswersByScreener((prev) => ({
        ...prev,
        [templateId]: {
          ...(prev[templateId] || {}),
          [answerKey]: value,
        },
      }));
    },
    [],
  );

  /* ---------------------- Build payload for a screener -------------- */

  const buildPayload = useCallback(
    (templateId: number | string): ScreenerSubmitPayload | null => {
      const questionnaire = screenerData?.questionnairescreeners.find(
        (q) => (q.templateid ?? q.questionnaireid) === templateId,
      );
      if (!questionnaire) return null;

      const isCustom = questionnaire.screenertype === 'CUSTOM';
      const answers = answersByScreener[templateId] || {};
      const questionPayload: { questionid: number | string; answer: string }[] = [];
      let score = 0;

      questionnaire.sections.forEach((section, sectionIndex) => {
        section.questionlist.forEach((q, qIndex) => {
          const hasOptions = q.options && q.options.length > 0;
          const hasPossibleAnswers = q.possibleanswers && q.possibleanswers.length > 0;
          if (!hasOptions && !hasPossibleAnswers && !isTextInputQuestion(q)) return;

          const key = `${templateId}_${sectionIndex}_${qIndex}`;
          const answer = answers[key];
          if (answer) {
            questionPayload.push({
              questionid: isCustom ? q.questionid : Number(q.questionid),
              answer,
            });

            score += calculateQuestionScore(q, answer);
          }
        });
      });

      if (questionPayload.length === 0) return null;

      const qId = isCustom
        ? (questionnaire.questionnaireid || questionnaire.templateid)
        : Number(questionnaire.questionnaireid ?? questionnaire.templateid);
      const tId = isCustom
        ? questionnaire.templateid
        : Number(questionnaire.templateid ?? questionnaire.questionnaireid);

      return {
        questionnaireId: qId,
        templateId: tId,
        questions: questionPayload,
        score: String(score),
      };
    },
    [screenerData, answersByScreener],
  );

  /* ---------------------- Submit single screener (accordion btn) --- */

  const handleSubmitScreener = useCallback(
    async (templateId: number | string) => {
      if (!appointmentId) return;

      const questionnaire = screenerData?.questionnairescreeners.find(
        (q) => (q.templateid ?? q.questionnaireid) === templateId,
      );
      if (!questionnaire) return;

      const answers = answersByScreener[templateId] || {};

      if (!screenerHasAllRequiredAnswers(questionnaire, templateId, answers)) {
        setBlockingError('Please answer all required questions before submitting this screener.');
        return;
      }

      const payload = buildPayload(templateId);
      if (!payload) return;

      if (inFlightSubmittingScreenersRef.current.has(templateId)) return;
      inFlightSubmittingScreenersRef.current.add(templateId);

      setSubmittingScreeners((prev) => {
        const s = new Set(prev);
        s.add(templateId);
        return s;
      });
      setLocalError(null);

      try {
        await withLoader('Saving screener...', async () => {
          await axiosInstance.put(
            `/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`,
            payload,
            { headers: { client: 'axis', domain: 'axis' } },
          );

          setSubmittedScreeners((prev) => {
            const s = new Set(prev);
            s.add(templateId);
            return s;
          });

          // Optimistically mark the matching SCREENERS step row completed
          // so the bar reacts instantly, then refetch the workflow for the
          // authoritative state. We stay on this page after saving a single
          // screener, so refreshWorkflow is the only thing that will update
          // the progress bar.
          markStepCompleted(
            (s) =>
              s.workflowType === WorkflowType.SCREENERS &&
              String(s.entityId) === String(templateId),
          );
          await refreshWorkflow();

          if (openScreener === templateId) setOpenScreener(null);
        });
      } catch (error: any) {
        setBlockingError(getApiErrorMessage(error, 'Unable to submit screener. Please try again.'));
      } finally {
        inFlightSubmittingScreenersRef.current.delete(templateId);
        setSubmittingScreeners((prev) => {
          const s = new Set(prev);
          s.delete(templateId);
          return s;
        });
      }
    },
    [appointmentId, screenerData, answersByScreener, openScreener, buildPayload, withLoader, markStepCompleted, refreshWorkflow, setBlockingError],
  );

  /* ---------------------- Navigate helper -------------------------- */

  const navigateToNext = useCallback(() => {
    if (isAllStepsCompleted()) {
      navigate(`/appointments/${appointmentId}/complete`);
    } else {
      const nextStep = getNextStepInSequence(location.pathname);
      if (nextStep) {
        navigate(nextStep.stepUrl);
      } else {
        navigate(`/appointments/${appointmentId}/complete`);
      }
    }
  }, [appointmentId, isAllStepsCompleted, getNextStepInSequence, location.pathname, navigate]);

  /* ---------------------- Continue button handler ------------------- */
  // When screeners loaded: block if required answers are missing; submit the
  // rest. Null payload with required answers OK is skippable (no dead-end).
  // When screener GET failed: allow Continue without forcing screeners.

  const handleContinue = async () => {
    if (inFlightContinueRef.current) return;
    inFlightContinueRef.current = true;
    try {
    const questionnaires = screenerData?.questionnairescreeners ?? [];

    if (shouldAllowContinueWithoutScreeners(screenerLoadFailed, questionnaires)) {
      navigateToNext();
      return;
    }

    const incompleteNames = getIncompleteScreenerNames(
      questionnaires,
      submittedScreeners,
      answersByScreener,
    );

    if (incompleteNames.length > 0) {
      setBlockingError(
        `Please complete all wellness screeners before continuing. Incomplete: ${incompleteNames.join(', ')}`,
      );
      return;
    }

    // Collect payloads for un-submitted screeners with answers to submit.
    // Required-complete + empty payload → skip (do not block).
    const toSubmit: ScreenerSubmitPayload[] = [];
    questionnaires.forEach((q) => {
      const tid = q.templateid ?? q.questionnaireid;
      if (submittedScreeners.has(tid)) return;
      const payload = buildPayload(tid);
      if (payload) toSubmit.push(payload);
    });

    // All screeners already submitted via accordion (or nothing to POST) — advance
    if (toSubmit.length === 0) {
      navigateToNext();
      return;
    }

    // Submit all remaining un-submitted screeners
    setSubmitting(true);
    setLocalError(null);
    try {
      await withLoader('Submitting...', async () => {
        for (const qa of toSubmit) {
          try {
            await axiosInstance.put(
              `/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`,
              qa,
              { headers: { client: 'axis', domain: 'axis' } },
            );
          } catch (error: any) {
            throw new Error(getApiErrorMessage(error, 'Unable to submit screener. Please try again.'));
          }
        }

        // Bulk-mark every SCREENERS row optimistically, then refetch from
        // backend for definitive truth before navigating.
        markStepCompleted((s) => s.workflowType === WorkflowType.SCREENERS);
        await refreshWorkflow();

        navigateToNext();
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unable to save your information. Please try again.";
      setBlockingError(errorMsg);
    } finally {
      setSubmitting(false);
    }
    } finally {
      inFlightContinueRef.current = false;
    }
  };

  /* ---------------------- Save & Exit (kept as-is, currently commented out in UI) */

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    setSubmitting(true);
    setLocalError(null);
    try {
      await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(String(appointmentId))}/screener-questions`, { questionnaireId: 1, templateId: 1, questions: [], score: "0" }, {
        headers: { client: 'axis', domain: 'axis' }
      });

      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(reviewPath.replace("review-and-sign", "payments"));
        }
      }
    } catch (err) {
      setLocalError(getApiErrorMessage(err, "Unable to save your information. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CheckinLayout
      title="Wellness Check"
      subtitle="Help us understand how you've been feeling"
    >
        <div ref={pageTopRef} tabIndex={-1} />
        {(localError || workflowError) && (
          <div className="w-full max-w-[1200px] mb-2" role="alert">
            <Alert
              type={screenerLoadFailed && !workflowError ? "info" : "error"}
              onClose={() => {
                setLocalError(null);
              }}
            >
              {workflowError || localError}
            </Alert>
          </div>
        )}
        {allScreenersSubmitted ? (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4">
            <div className="flex items-start gap-3 bg-[#E0F2F3] border border-[#01545D] rounded-lg px-4 py-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
                ✓
              </div>
              <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
                <div className="font-semibold text-[#01545D]">All Screeners Already Submitted</div>
                <div>
                  All wellness check screeners have already been completed and submitted. You can proceed to the next step.
                </div>
              </div>
            </div>
            <div className="flex gap-5 w-full mt-8">
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => {
                  const previousStep = getPreviousStep(location.pathname);
                  if (previousStep) {
                    navigate(previousStep.stepUrl);
                  } else {
                    navigate(medicalPath);
                  }
                }}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                Back
              </AppButton>
              <AppButton
                type="button"
                onClick={() => {
                  if (isAllStepsCompleted()) {
                    navigate(`/appointments/${appointmentId}/complete`);
                  } else {
                    const nextStep = getNextStepInSequence(location.pathname);
                    if (nextStep) {
                      navigate(nextStep.stepUrl);
                    } else {
                      navigate(`/appointments/${appointmentId}/complete`);
                    }
                  }
                }}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                Continue
              </AppButton>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4">
            {/* Screener accordion list */}
            <section className="flex flex-col gap-4">
              {screenerData?.questionnairescreeners?.map((questionnaire) => {
                const tid = questionnaire.templateid ?? questionnaire.questionnaireid;
                return (
                  <ScreenerFormItem
                    key={tid}
                    questionnaire={questionnaire}
                    isOpen={openScreener === tid}
                    isSubmitted={submittedScreeners.has(tid)}
                    isSubmitting={submittingScreeners.has(tid)}
                    selectedAnswers={answersByScreener[tid] || {}}
                    onToggleOpen={() => setOpenScreener(openScreener === tid ? null : tid)}
                    onAnswerChange={(key, value) => handleAnswerChange(tid, key, value)}
                    onSubmit={handleSubmitScreener}
                  />
                );
              })}
            </section>

            <div className="flex items-start gap-3 bg-[#F7F8FD] border border-[rgba(1,84,93,0.3)] rounded-lg px-4 py-3 mt-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#01545D] text-white text-xs font-bold mt-1">
                i
              </div>
              <div className="flex flex-col gap-1 text-sm text-[#1C1C1C]">
                <div className="font-semibold text-[#01545D]">Your Privacy Matters</div>
                <div>
                  These questions help your provider assess your mental well-being. Your responses are confidential
                  and protected by HIPAA.
                </div>
              </div>
            </div>

            <div className="flex gap-5 w-full mt-8">
              <AppButton
                variant="secondary"
                type="button"
                onClick={() => {
                  const previousStep = getPreviousStep(location.pathname);
                  if (previousStep) {
                    navigate(previousStep.stepUrl);
                  } else {
                    navigate(medicalPath);
                  }
                }}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                Back
              </AppButton>
              {/* <AppButton
                variant="secondary"
                type="button"
                onClick={handleSaveAndExit}
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? "Saving..." : "Save & Exit"}
              </AppButton> */}
              <AppButton
                type="button"
                onClick={handleContinue}
                disabled={!!workflowError || submitting}
                className="flex-1"
              >
                {submitting ? "Saving..." : "Continue"}
              </AppButton>
            </div>
          </div>
        )}
    </CheckinLayout>
  );
};

export default WellnessCheck;
